import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Pencil, LogOut, X } from "lucide-react";

const empty = { title: "", description: "", status: "active", image_url: "", goal: "", order: 0 };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = () => api.get("/campaigns").then((r) => setCampaigns(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (c) => {
    setForm({ title: c.title, description: c.description, status: c.status, image_url: c.image_url || "", goal: c.goal || "", order: c.order || 0 });
    setEditing(c._id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await api.post("/campaigns", payload);
      else await api.put(`/campaigns/${editing}`, payload);
      toast.success("Campaign saved");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#4A4E69]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E5DCC5] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full overflow-hidden bg-white shrink-0"><img src={IMAGES.logo} alt="logo" className="h-full w-full object-cover scale-[1.18]" /></span>
            <div>
              <p className="font-display text-lg font-bold text-[#2B2D42]">Campaign Manager</p>
              <p className="text-xs text-[#4A4E69]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-[#4A4E69] hover:text-[#2B2D42]">View site</a>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-[#E5DCC5] px-4 py-2 text-sm font-medium text-[#2B2D42] hover:bg-[#F4EBD9]">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-bold text-[#2B2D42]">Campaigns</h1>
          <button onClick={openNew} data-testid="new-campaign-btn" className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-5 py-2.5 font-semibold text-white hover:bg-[#D06A4F] active:scale-95">
            <Plus className="h-4 w-4" /> New Campaign
          </button>
        </div>

        <div className="grid gap-4">
          {campaigns.map((c) => (
            <div key={c._id} className="flex items-center gap-4 rounded-2xl border border-[#E5DCC5] bg-white p-4" data-testid="admin-campaign-row">
              {c.image_url && <img src={c.image_url} alt="" className="h-16 w-16 rounded-xl object-cover" />}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#F4EBD9] px-2.5 py-0.5 text-xs font-semibold uppercase text-[#4A4E69]">{c.status}</span>
                  <h3 className="font-display text-lg font-bold text-[#2B2D42]">{c.title}</h3>
                </div>
                <p className="text-sm text-[#4A4E69] line-clamp-1">{c.description}</p>
              </div>
              <button onClick={() => openEdit(c)} data-testid="edit-campaign-btn" className="rounded-lg p-2 text-[#4A4E69] hover:bg-[#F4EBD9]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(c._id)} data-testid="delete-campaign-btn" className="rounded-lg p-2 text-[#E07A5F] hover:bg-[#FDE8DF]"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {campaigns.length === 0 && <p className="text-[#4A4E69]">No campaigns yet. Add your first one!</p>}
        </div>
      </main>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2D42]/50 p-4" data-testid="campaign-modal">
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-[#2B2D42]">{editing === "new" ? "New Campaign" : "Edit Campaign"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1.5 hover:bg-[#F4EBD9]"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              {[
                { k: "title", label: "Title" },
                { k: "goal", label: "Goal / tagline (optional)" },
                { k: "image_url", label: "Image URL (optional)" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs font-medium uppercase tracking-wide text-[#4A4E69]">{f.label}</label>
                  <input value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`field-${f.k}`} required={f.k === "title"} className="mt-1.5 w-full rounded-xl border border-[#E5DCC5] px-4 py-2.5 outline-none focus:border-[#E07A5F]" />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-[#4A4E69]">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="field-description" required rows={3} className="mt-1.5 w-full rounded-xl border border-[#E5DCC5] px-4 py-2.5 outline-none focus:border-[#E07A5F]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-[#4A4E69]">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="field-status" className="mt-1.5 w-full rounded-xl border border-[#E5DCC5] px-4 py-2.5 outline-none focus:border-[#E07A5F] bg-white">
                    <option value="active">Active</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-[#4A4E69]">Order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="field-order" className="mt-1.5 w-full rounded-xl border border-[#E5DCC5] px-4 py-2.5 outline-none focus:border-[#E07A5F]" />
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="save-campaign-btn" className="w-full rounded-full bg-[#E07A5F] px-6 py-3 font-semibold text-white hover:bg-[#D06A4F] active:scale-95 disabled:opacity-60">
                {saving ? "Saving…" : "Save Campaign"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
